<template>
  <div class="social-snapshot-widget">
    <!-- Standard page header -->
    <div class="standard-page-header standard-page-header--no-bottom-line social-snapshot-widget__header">
      <h2 class="global-h2 standard-page-header__title">
        <RouterLink
          :to="{
            name: 'SuggestedSocialPosts',
            params: { pageId: props.customerId },
          }"
        >
          Social Snapshots<BetaBadge class="standard-page-header__beta-badge" style="margin-left: 7px" />
        </RouterLink>
      </h2>

      <div class="standard-page-header__subtitle-and-link">
        <div class="standard-page-header__subtitle">Captivate your audience with vibrant, weekly content-powered by your adventures and enhanced by your favorite photos.</div>
      </div>
    </div>
    <!-- / Standard page header -->

    <Loader v-if="isLoadingAllManuallyUploadedMedia || isLoadingAllPosts" />

    <div v-else-if="socialMediaPosts?.length" class="social-snapshot-widget__posts-list">
      <SocialPostPreview
        class="social-snapshot-widget__post"
        v-for="socialPost in socialMediaPosts.slice(0, howManyPostsToShow)"
        :key="socialPost?.id"
        :assetIds="socialPost.assetIds"
        :caption="socialPost.caption || ''"
        :isWithTextarea="false"
        :isWithShareAndDislikeButtons="true"
        @clickCustomizeAndShare="
          () => {
            postToEdit = socialPost;
            isEditAndShareSocialPostModalVisible = true;
          }
        "
        @clickDislike="
          () => {
            postIdToDislike = socialPost?.id;
            isDislikeModalVisible = true;
          }
        "
        @updatePostUrl="
          (newUrl, etag) => {
            socialPost.postUrl = newUrl;
            socialPost._etag = etag;
          }
        "
        :fancyboxKey="socialPost?.id"
        :post="socialPost"
      />

      <SocialPostEditAndShareModal
        v-if="isEditAndShareSocialPostModalVisible"
        v-model:isVisible="isEditAndShareSocialPostModalVisible"
        :postToEdit="postToEdit"
        :customerId="props.customerId"
        @close="isEditAndShareSocialPostModalVisible = false"
        @savePost="() => {}"
      />

      <DislikePostConfirmModal v-if="isDislikeModalVisible" v-model:isVisible="isDislikeModalVisible">
        <template #footer="{ closeModal, combinedFeedbackString }">
          <SrpButton @click="closeModal" color="gray" fill="outlined">Cancel</SrpButton>

          <SrpButton
            @click="
              async () => {
                await dislikeAndRemovePost(postIdToDislike, combinedFeedbackString);
                closeModal();
              }
            "
          >
            Remove Post
          </SrpButton>
        </template>
      </DislikePostConfirmModal>
    </div>

    <BigDragAndDropAreaWithThumbs
      v-else
      class="social-snapshot-widget__drag-and-drop-area"
      :customerId="props.customerId"
      :imagesLimitRange="[MinImagesNumber, 0]"
      v-model:imagesList="allManuallyUploadedMedia"
      flow="customer-upload"
    >
      <template #guidanceSchema="{ isUploadInProgress, imagesListLength }">
        <template v-if="!imagesListLength && !isUploadInProgress">
          <div style="width: calc(100% - 30px); max-width: 520px; margin-bottom: 25px; border-bottom: 1px rgba(0, 0, 0, 0.15) solid; pointer-events: none"></div>

          <AiPostGenerationVisualSchema :isWithCtaButton="false" size="tiny" circlesColor="white" bgColor="pale-blue" />
        </template>
      </template>
    </BigDragAndDropAreaWithThumbs>

    <SrpButton
      v-if="socialMediaPosts.length"
      :isDisabled="props.isCustomerDisabled"
      tag="RouterLink"
      fill="outlined"
      class="social-snapshot-widget__generate-post-button"
      :to="{
        name: 'SuggestedSocialPosts',
        params: { pageId: props.customerId },
        hash: `#isOpenGenModal=true`,
      }"
    >
      <template v-if="isSendingGenerateRequest" #icon><LoadingIcon :size="20" /></template>
      Generate more Posts
    </SrpButton>

    <SrpButton
      v-else
      class="social-snapshot-widget__generate-post-button"
      :isDisabled="allManuallyUploadedMedia.length < MinImagesNumber || isLoadingAllManuallyUploadedMedia || isSendingGenerateRequest || props.isCustomerDisabled"
      @click="generatePost"
    >
      <template v-if="isSendingGenerateRequest" #icon><LoadingIcon :size="20" /></template>
      Generate Post
    </SrpButton>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";
import { MetricSender } from "@helpers/MetricSender";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import Loader from "@components/Loader/Loader.vue";
import BigDragAndDropAreaWithThumbs from "@components/BigDragAndDropAreaWithThumbs.vue";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";
import SocialPostEditAndShareModal from "@components/SocialPostEditAndShareModal/index.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import DislikePostConfirmModal from "@components/SocialPostPreview/DislikePostConfirmModal.vue";
import BetaBadge from "@components/BetaBadge.vue";
import AiPostGenerationVisualSchema from "@components/AiPostGenerationVisualSchema.vue";

// Types
import { SocialMediaPostData } from "@contracts/shareStop";

const props = withDefaults(
  defineProps<{
    customerId: string;
    isCustomerDisabled: boolean;
  }>(),
  {
    customerId: "",
    isCustomerDisabled: false,
  }
);

// Post to edit ===============================================================
const postToEdit = ref<SocialMediaPostData | null>(null);

// Toggle dislike modal =======================================================
const postIdToDislike = ref<string>("");
const isDislikeModalVisible = ref<boolean>(false);

// Toggle "Edit and share social post" modal ==================================
const isEditAndShareSocialPostModalVisible = ref<boolean>(false);

// Min images number ==========================================================
const MinImagesNumber = 20;

// Load all customer manually uploaded media ==================================
const isLoadingAllManuallyUploadedMedia = ref<boolean>(false);
const allManuallyUploadedMedia = ref<Array<string>>([]);

async function loadMedia() {
  isLoadingAllManuallyUploadedMedia.value = true;
  allManuallyUploadedMedia.value = await PhotoGalleryV2Repo.getAllPhotosFromAllGalleries(props.customerId);
  isLoadingAllManuallyUploadedMedia.value = false;
}

// How many posts to show =====================================================
const howManyPostsToShow = 3;

// Load manually created posts ================================================
const isLoadingAllPosts = ref<boolean>(true);
const socialMediaPosts = ref<Array<SocialMediaPostData>>([]);

async function loadPosts() {
  isLoadingAllPosts.value = true;

  const postsResult = await ShareStopSummaryRepo.loadLatestWeeklyPosts(props.customerId);
  socialMediaPosts.value = postsResult ?? [];

  isLoadingAllPosts.value = false;
}

// Load posts and media files if needed =======================================
async function loadPostsAndMedia() {
  await loadPosts();

  // Media files are needed only when the Posts list is empty
  if (!socialMediaPosts.value?.length) {
    await loadMedia();
  }
}

onMounted(loadPostsAndMedia);

watch(() => props.customerId, loadPostsAndMedia);

// Generate post ==============================================================
const isSendingGenerateRequest = ref<boolean>(false);

async function generatePost() {
  isSendingGenerateRequest.value = true;
  const results = await ShareStopSummaryRepo.genSocialPosts(props.customerId, allManuallyUploadedMedia.value, "", 2, true, true);
  isSendingGenerateRequest.value = false;

  socialMediaPosts.value = results.results.map(p => p.post);
}

// Dislike post ===============================================================
async function dislikeAndRemovePost(postId: string, reasonText: string) {
  // Check in manually created posts
  const postIndex = socialMediaPosts.value.findIndex(p => p.id === postId);
  if (postIndex !== -1) {
    socialMediaPosts.value.splice(postIndex, 1);
  }

  await ShareStopSummaryRepo.deletePost(props.customerId, postId, reasonText);

  MetricSender.sendMetric("Cust-SocialPost-Delete", { socialPostId: postId });
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Social snapshots widget ====================================================
.social-snapshot-widget {
  &__header {
    margin-bottom: 25px;
  }

  &__posts-list {
    gap: $desktop-wide-grid-gap-width;
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
  }

  &__post {
    width: calc(33.3% - $desktop-wide-grid-gap-width * 2 / 3);
  }

  &__drag-and-drop-area {
    margin-bottom: 20px;
  }

  &__generate-post-button {
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .social-snapshot-widget {
    &__posts-list {
      gap: $laptop-grid-gap-width;
    }

    &__post {
      width: calc(33.3% - $laptop-grid-gap-width * 2 / 3);
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .social-snapshot-widget {
    &__posts-list {
      gap: $tablet-large-grid-gap-width;
    }

    &__post {
      width: calc(33.3% - $tablet-large-grid-gap-width * 2 / 3);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .social-snapshot-widget {
    &__posts-list {
      gap: $tablet-grid-gap-width;
    }

    &__post {
      width: calc(50% - $tablet-grid-gap-width * 1 / 2);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .social-snapshot-widget {
    &__post {
      width: 100%;
    }
  }
}
</style>
